// client/src/components/admin/ImageModerationQueue.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardMedia, CardContent, CardActions,
  Button, Chip, CircularProgress, TextField, Alert, Pagination,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';

/**
 * Image moderation queue for Trust Level 4+ users and admins.
 * Approve or reject pending catalogue image submissions.
 */
export default function ImageModerationQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState(null);
  const [rejectionReasons, setRejectionReasons] = useState({});  // enum value per item
  const [rejectionNotes, setRejectionNotes] = useState({});      // optional note per item

  const fetchQueue = async (p = 1) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/reputation/moderation/image-queue?page=${p}`);
      setItems(res.data.items);
      setPages(res.data.pages);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Trust Level 4 required to access this queue.' : 'Failed to load queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQueue(page); }, [page]);

  const handleApprove = async (id) => {
    try {
      await axiosInstance.post(`/reputation/moderation/image-queue/${id}/approve`);
      setItems(prev => prev.filter(i => i._id !== id));
    } catch (err) { console.error('Approve error:', err); }
  };

  const handleReject = async (id) => {
    const reason = rejectionReasons[id];
    if (!reason) return; // frontend validation: reason is required
    try {
      await axiosInstance.post(`/reputation/moderation/image-queue/${id}/reject`, {
        reason,
        note: rejectionNotes[id] || undefined,
      });
      setItems(prev => prev.filter(i => i._id !== id));
    } catch (err) { console.error('Reject error:', err); }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (loading) return <CircularProgress />;
  if (items.length === 0) return <Typography color="text.secondary">No pending submissions.</Typography>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Image Moderation Queue ({items.length})</Typography>
      {items.map(item => (
        <Card key={item._id} sx={{ mb: 2, display: 'flex', gap: 2, p: 1 }}>
          <CardMedia
            component="img"
            image={item.imageUrl}
            alt="Submission"
            sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1 }}
          />
          <Box sx={{ flex: 1 }}>
            <CardContent sx={{ p: 0 }}>
              <Typography variant="body2">
                Submitted by <strong>{item.userId?.username}</strong>
                <Chip label={`Trust ${item.userId?.trustLevel}`} size="small" sx={{ ml: 1 }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.collectionType} · {new Date(item.createdAt).toLocaleDateString()}
              </Typography>
              {item.itemId && item.collectionType && (
                <Box sx={{ mt: 0.5, mb: 0.5 }}>
                  <RouterLink to={`/item/${item.collectionType}/${item.itemId}`} style={{ fontSize: 12 }}>
                    View item →
                  </RouterLink>
                </Box>
              )}
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>Rejection reason</InputLabel>
                <Select
                  label="Rejection reason"
                  value={rejectionReasons[item._id] || ''}
                  onChange={e => setRejectionReasons(prev => ({ ...prev, [item._id]: e.target.value }))}
                >
                  <MenuItem value="wrong_item">Wrong Item</MenuItem>
                  <MenuItem value="low_quality">Low Quality</MenuItem>
                  <MenuItem value="inappropriate">Inappropriate Content</MenuItem>
                  <MenuItem value="duplicate">Duplicate Submission</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small" fullWidth placeholder="Additional notes (optional, 300 chars)"
                value={rejectionNotes[item._id] || ''}
                onChange={e => setRejectionNotes(prev => ({ ...prev, [item._id]: e.target.value }))}
                inputProps={{ maxLength: 300 }}
                sx={{ mt: 1 }}
              />
            </CardContent>
            <CardActions sx={{ p: 0, mt: 1 }}>
              <Button variant="contained" color="success" size="small" onClick={() => handleApprove(item._id)}>Approve</Button>
              <Button
                variant="outlined" color="error" size="small"
                onClick={() => handleReject(item._id)}
                disabled={!rejectionReasons[item._id]}
              >
                Reject
              </Button>
            </CardActions>
          </Box>
        </Card>
      ))}
      {pages > 1 && <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} />}
    </Box>
  );
}
